import type { ActiveCall, MessageThread } from '../types';

// A call is "live" while at least one participant has pinged recently. Clients
// ping every HEARTBEAT_MS; a crashed/killed app simply stops pinging and the
// call goes stale on its own - no server cleanup needed.
export const CALL_HEARTBEAT_MS = 15000;
export const CALL_STALE_MS = 45000;
// How long an unanswered 1:1 call rings before giving up.
export const CALL_RING_TIMEOUT_MS = 45000;

export const freshCallParticipantIds = (call: ActiveCall | undefined | null, now = Date.now()): string[] =>
  call ? Object.entries(call.participants || {}).filter(([, ts]) => now - Number(ts) < CALL_STALE_MS).map(([id]) => id) : [];

export const getActiveCall = (thread: Pick<MessageThread, 'activeCall'> | null | undefined, now = Date.now()): ActiveCall | null => {
  const call = thread?.activeCall;
  if (!call?.id) return null;
  return freshCallParticipantIds(call, now).length > 0 ? call : null;
};

export const formatCallDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// The one and only ringtone: public/sounds/ringtone.mp3 (the RIDAZ tone), looped while a call rings.
// Browsers refuse to play sound until the user has interacted with the page, so a single shared
// audio element is "unlocked" on the first tap/click anywhere (primeRingtone) and reused later.
const RINGTONE_SRC = '/sounds/ringtone.mp3';
let ringtoneAudio: HTMLAudioElement | null = null;
let primed = false;
// Bumped whenever a real ring starts so a still-pending unlock can't pause it.
let ringGeneration = 0;

const getRingtoneAudio = (): HTMLAudioElement => {
  if (!ringtoneAudio) {
    ringtoneAudio = new Audio();
    ringtoneAudio.loop = true;
    ringtoneAudio.preload = 'none';
    ringtoneAudio.src = RINGTONE_SRC;
  }
  return ringtoneAudio;
};

// A 0.1s silent clip used only to unlock audio playback, so the real ringtone is never loaded into the element
// (and can never be heard) when the app opens.
const SILENT_SRC = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

// Call once at startup: unlocks audio playback on the first user gesture, silently.
export const primeRingtone = (): void => {
  if (primed || typeof window === 'undefined') return;
  const unlock = () => {
    if (primed) return;
    primed = true;
    const audio = getRingtoneAudio();
    const gen = ringGeneration;
    // Never touch the element if a real ring has already started.
    if (!audio.paused) { window.removeEventListener('pointerdown', unlock); window.removeEventListener('keydown', unlock); return; }
    audio.loop = false;
    audio.muted = true;
    audio.src = SILENT_SRC;
    const restore = () => {
      if (gen !== ringGeneration) return;
      audio.pause(); audio.currentTime = 0; audio.muted = false; audio.loop = true; audio.src = RINGTONE_SRC;
    };
    audio.play().then(restore).catch(() => { restore(); primed = false; });
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock, { once: false });
  window.addEventListener('keydown', unlock, { once: false });
};

// Starts the ringtone on loop. Returns a stop function.
export const startRingtone = (volume = 1): (() => void) => {
  ringGeneration++;
  const audio = getRingtoneAudio();
  if (!audio.src.endsWith(RINGTONE_SRC)) audio.src = RINGTONE_SRC;
  audio.loop = true;
  audio.muted = false;
  audio.currentTime = 0;
  audio.volume = volume;
  audio.play().catch(() => {
    // Still blocked (no tap on the page yet): stay silent - the on-screen ring still shows.
  });
  // Safety net: a ringtone must never outlive a ring window, whatever the UI state does.
  const failsafe = window.setTimeout(() => { audio.pause(); audio.currentTime = 0; }, 45000);
  return () => {
    window.clearTimeout(failsafe);
    audio.pause();
    audio.currentTime = 0;
  };
};
